// DrawerForm — the drawer CONTAINER for the shared form shell (ADR-038): a
// SlideOver (logical end) hosting one useFormShell body. The "90% without leaving
// context" surface — simple creates + contextual single-section edits launched
// from lists, peeks, or detail pages (COMPONENTS §Form surfaces).
//
// The consumer owns the shell (so its fields register / Controller against
// shell.form); DrawerForm wires the SlideOver frame around it: every open RE-SEEDS
// the form from the row as it is then (ADR-084 §6 — a drawer kept mounted never
// shows the last save's values, and the stamp is taken with the values); every
// close path (Escape / backdrop / X / Cancel) routes through the structural dirty
// guard (shell.requestLeave); the gone line's Close leaves without it; the discard
// ConfirmDialog renders as a SIBLING of the SlideOver — so confirming discard
// (which closes the drawer) doesn't unmount the dialog mid-transition. A drawer
// never changes the route, so it needs no route-leave blocker.

import { useEffect, type ReactNode } from 'react';
import type { FieldValues } from 'react-hook-form';
import { SlideOver } from '../../ui/SlideOver.js';
import { FormBody, FormFooter } from './FormShell.js';
import type { UseFormShellReturn } from './useFormShell.js';

export interface DrawerFormProps<TValues extends FieldValues> {
  shell: UseFormShellReturn<TValues>;
  open: boolean;
  /** The raw close action (e.g. setOpen(false)). Every close path routes through
   *  the dirty guard first, so unsaved edits prompt to discard. */
  onClose: () => void;
  title: string;
  description?: string;
  /** The submit verb ("Create site", "Save changes") — i18n'd by the caller. */
  submitLabel: string;
  cancelLabel?: string;
  /** Holds Save while a choice the form shows as blocked stands (a decision form disables, never surprise-fails). */
  submitDisabled?: boolean;
  /** The field stack — registers / Controllers against shell.form. */
  children: ReactNode;
  /** Tailwind max-w-* for the panel; falls back to SlideOver's default. */
  maxWidth?: string;
  /** Field name to focus + text-select when the drawer opens — the open-to-edit
   *  pattern (click a row → land in the editable, selected field). Shared by every
   *  edit drawer so they behave identically; omit for create-from-scratch flows. */
  autoFocusField?: string;
}

export function DrawerForm<TValues extends FieldValues>({
  shell,
  open,
  onClose,
  title,
  description,
  submitLabel,
  cancelLabel,
  submitDisabled,
  children,
  maxWidth,
  autoFocusField,
}: DrawerFormProps<TValues>) {
  // Every open starts from the row as it is NOW and without the last attempt's line. An RHF reset runs in an
  // effect, never during render — a passive one, after the shell's own layout effect has taken this render's row.
  const { reseed } = shell;
  useEffect(() => {
    if (open) reseed();
  }, [open, reseed]);

  // Focus + select the named field when the drawer opens, so an open-to-edit
  // drawer lands in the editable, selected field. Run on the next frame (after
  // React Aria settles its own open-focus + the reseed) and target the field by
  // `name` (RHF register sets it) within the open dialog — only one drawer is
  // open at a time.
  useEffect(() => {
    if (!open || !autoFocusField) return;
    const id = window.setTimeout(() => {
      // A native input by its `name`; a picker's trigger by its `data-field` (the family's field carries no `name`).
      const el = document.querySelector<HTMLInputElement>(`[role="dialog"] [name="${autoFocusField}"], [role="dialog"] [data-field="${autoFocusField}"]`);
      if (el) {
        el.focus();
        el.select?.();
      }
    }, 0);
    return () => clearTimeout(id);
  }, [open, autoFocusField]);

  return (
    <>
      <SlideOver
        open={open}
        onClose={() => (shell.gone ? shell.discardAndLeave(onClose) : shell.requestLeave(onClose))}
        title={title}
        description={description}
        maxWidth={maxWidth}
        footer={
          <FormFooter
            shell={shell}
            onCancel={onClose}
            submitLabel={submitLabel}
            cancelLabel={cancelLabel}
            submitDisabled={submitDisabled}
          />
        }
      >
        <FormBody shell={shell} onGone={onClose}>
          {children}
        </FormBody>
      </SlideOver>
      {shell.discardDialog}
    </>
  );
}
